import { getApiBase } from "../utils/apiBase";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";


// -------------------- helpers --------------------
const jsonHeaders = (): HeadersInit => ({
  "Content-Type": "application/json",
  "X-CSRFToken": Cookies.get("csrftoken") || "",
});

// -------------------- CRUD --------------------
export const createReport = async (
  projectId: string,
  title?: string,
): Promise<{ id: string; projectId: string; title: string }> => {
  const res = await fetch(`${getApiBase()}/api/projects/${projectId}/reports`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()).data;
};

export const deleteReport = async (projectId: string, reportId: string): Promise<void> => {
  const res = await fetch(`${getApiBase()}/api/projects/${projectId}/reports/${reportId}`, {
    method: "DELETE",
    credentials: "include",
    headers: jsonHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
};

export const listReports = async (projectId: string): Promise<any[]> => {
  const res = await fetch(`${getApiBase()}/api/projects/${projectId}/reports`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()).data;
};

export const getReport = async (
  projectId: string,
  reportId: string,
): Promise<any> => {
  const res = await fetch(`${getApiBase()}/api/projects/${projectId}/reports/${reportId}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()).data;
};

export const saveReport = async (
  projectId: string,
  reportId: string,
  payload: Partial<{ title: string; body_markdown: string }>,
): Promise<any> => {
  const res = await fetch(`${getApiBase()}/api/projects/${projectId}/reports/${reportId}`, {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()).data;
};

// -------------------- hooks --------------------
export const useCreateReport = (projectId: string) => {
  return useMutation({
    mutationFn: (title?: string) => createReport(projectId, title),
  });
};

export const useReport = (projectId: string, reportId: string) => {
  return useQuery({
    queryKey: ["report", projectId, reportId],
    queryFn: () => getReport(projectId, reportId),
    enabled: !!projectId && !!reportId,
  });
};

export const useReports = (projectId: string, enabled = true) => {
  return useQuery({
    queryKey: ["reports", projectId],
    queryFn: () => listReports(projectId),
    enabled: !!projectId && enabled,
  });
};

export const useSaveReport = (projectId: string, reportId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<{ title: string; body_markdown: string }>) =>
      saveReport(projectId, reportId, payload),
    onSuccess: (data) => {
      qc.setQueryData(["report", projectId, reportId], data);
    },
  });
};

export const useDeleteReport = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) => deleteReport(projectId, reportId),
    onSuccess: (_, reportId) => {
      qc.invalidateQueries({ queryKey: ["reports", projectId] });
      qc.removeQueries({ queryKey: ["report", projectId, reportId] });
    },
  });
};
